import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { paymentApi } from '../services/api';
import { Transaction, BillingBalance, BillingStats } from '../types/api';

interface BillingCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

const BillingCenter: React.FC<BillingCenterProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'all' | 'income' | 'expense'>('all');
  const [isLoading, setIsLoading] = useState(false);
  const [balance, setBalance] = useState<BillingBalance>({
    totalIncome: 0,
    totalWithdraw: 0,
    availableBalance: 0,
    frozenBalance: 0
  });
  const [stats, setStats] = useState<BillingStats>({
    totalIncome: 0,
    totalExpense: 0,
    incomeCount: 0,
    expenseCount: 0
  });
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [hasMore, setHasMore] = useState(false);

  // 加载账单数据
  const loadBillingData = useCallback(async (type: string, offset = 0, append = false) => {
    setIsLoading(true);
    try {
      const result = await paymentApi.getBilling({
        type: type === 'all' ? undefined : type,
        limit: 20,
        offset
      });

      if (result.success && result.data) {
        setBalance(result.data.balance);
        setStats(result.data.stats);
        
        if (append) {
          setTransactions(prev => [...prev, ...result.data.transactions]);
        } else {
          setTransactions(result.data.transactions);
        }
        
        setPagination(result.data.pagination);
        setHasMore(offset + result.data.transactions.length < result.data.pagination.total);
      }
    } catch (error: any) {
      toast.error(error.message || '加载账单失败');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 切换标签时重新加载
  useEffect(() => {
    if (isOpen) {
      loadBillingData(activeTab);
    }
  }, [isOpen, activeTab, loadBillingData]);

  // 加载更多
  const handleLoadMore = () => {
    if (hasMore && !isLoading) {
      loadBillingData(activeTab, pagination.offset + pagination.limit, true);
    }
  };

  // 获取交易类型图标和颜色
  const getTransactionStyle = (type: string) => {
    switch (type) {
      case 'income':
        return { icon: 'fa-arrow-down', color: 'text-emerald-500', bgColor: 'bg-emerald-100 dark:bg-emerald-900/30' };
      case 'expense':
        return { icon: 'fa-arrow-up', color: 'text-red-500', bgColor: 'bg-red-100 dark:bg-red-900/30' };
      case 'withdraw':
        return { icon: 'fa-wallet', color: 'text-blue-500', bgColor: 'bg-blue-100 dark:bg-blue-900/30' };
      case 'refund':
        return { icon: 'fa-undo', color: 'text-amber-500', bgColor: 'bg-amber-100 dark:bg-amber-900/30' };
      default:
        return { icon: 'fa-exchange-alt', color: 'text-gray-500', bgColor: 'bg-gray-100 dark:bg-gray-700' };
    }
  };

  // 格式化日期
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `今天 ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `昨天 ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays}天前`;
    } else {
      return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="relative bg-white dark:bg-gray-800 p-6 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
              >
                <i className="fas fa-times text-gray-500 dark:text-gray-400 text-sm"></i>
              </button>
              <div className="text-center">
                <i className="fas fa-wallet text-4xl mb-3 text-primary"></i>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">账单中心</h2>
              </div>
            </div>

            {/* 余额卡片 */}
            <div className="p-6 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-750">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <i className="fas fa-coins text-emerald-500 mr-1"></i>
                    总收入
                  </div>
                  <div className="text-xl font-bold text-emerald-600">
                    ¥{stats.totalIncome.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{stats.incomeCount} 笔</div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <i className="fas fa-shopping-cart text-red-500 mr-1"></i>
                    总支出
                  </div>
                  <div className="text-xl font-bold text-red-600">
                    ¥{stats.totalExpense.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{stats.expenseCount} 笔</div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <i className="fas fa-wallet text-blue-500 mr-1"></i>
                    可用余额
                  </div>
                  <div className="text-xl font-bold text-blue-600">
                    ¥{balance.availableBalance.toFixed(2)}
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <i className="fas fa-arrow-circle-up text-purple-500 mr-1"></i>
                    已提现
                  </div>
                  <div className="text-xl font-bold text-purple-600">
                    ¥{balance.totalWithdraw.toFixed(2)}
                  </div>
                </div>
              </div>
              
              {/* 提现说明 */}
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                <div className="flex items-start">
                  <i className="fas fa-info-circle text-amber-500 mt-0.5 mr-2"></i>
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    自动提现功能正在设计中，如需提现，请联系客服申请手动提现，将在1-2个工作日内打款到您的账户。
                  </p>
                </div>
              </div>
            </div>

            {/* 交易记录标签 */}
            <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex space-x-1">
                {[
                  { key: 'all', label: '全部', icon: 'fa-list' },
                  { key: 'income', label: '收入', icon: 'fa-arrow-down' },
                  { key: 'expense', label: '支出', icon: 'fa-arrow-up' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as 'all' | 'income' | 'expense')}
                    className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-all ${
                      activeTab === tab.key
                        ? 'bg-primary text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <i className={`fas ${tab.icon} mr-1`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 交易列表 */}
            <div className="overflow-y-auto max-h-[40vh] p-6">
              {isLoading && transactions.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-12">
                  <i className="fas fa-receipt text-4xl text-gray-300 dark:text-gray-600 mb-3"></i>
                  <p className="text-gray-500 dark:text-gray-400">暂无交易记录</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map(transaction => {
                    const style = getTransactionStyle(transaction.type);
                    return (
                      <motion.div
                        key={transaction.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-650 transition-colors"
                      >
                        <div className={`w-10 h-10 rounded-full ${style.bgColor} flex items-center justify-center mr-4`}>
                          <i className={`fas ${style.icon} ${style.color}`}></i>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {transaction.description}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                            {transaction.itemType && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-200 dark:bg-gray-600 mr-2">
                                {transaction.itemType === 'prompt' ? '提示词' : '智能体'}
                              </span>
                            )}
                            {transaction.relatedUsername && (
                              <span className="mr-2">
                                <i className="fas fa-user text-xs mr-1"></i>
                                {transaction.relatedUsername}
                              </span>
                            )}
                            <span>{formatDate(transaction.createdAt)}</span>
                          </div>
                        </div>
                        
                        <div className={`text-lg font-semibold ${
                          transaction.amount > 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {transaction.amount > 0 ? '+' : ''}¥{Math.abs(transaction.amount).toFixed(2)}
                        </div>
                      </motion.div>
                    );
                  })}
                  
                  {/* 加载更多 */}
                  {hasMore && (
                    <div className="text-center pt-4">
                      <button
                        onClick={handleLoadMore}
                        disabled={isLoading}
                        className="px-6 py-2 text-sm font-medium text-primary hover:text-primary-dark transition-colors disabled:opacity-50"
                      >
                        {isLoading ? (
                          <>
                            <i className="fas fa-spinner fa-spin mr-2"></i>
                            加载中...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-chevron-down mr-2"></i>
                            加载更多
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 底部 */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                共 {pagination.total} 条记录
              </div>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors"
              >
                关闭
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BillingCenter;

